import _ from "lodash";
import { Season, SeasonAndYear, Sport } from "./types";
import { buildDisplayName, buildShortDisplayName } from "src/utils/build-display-name";
import * as Team from "src/state/modules/team";

const winterSeasonDisplay = (season: Season, year: number, fullYear: boolean) => {
  const secondYear = fullYear ? `${year + 1}` : `${(year + 1).toString().slice(2)}`;
  return `${season} ${year}-${secondYear}`;
};

const nonWinterSeasonDisplay = (season: Season, year: number) => `${season} ${year}`;

export const buildDisplaySeason = (season: Season, year: number, fullYear = false): string => {
  return _.capitalize(
    season === Season.WINTER ? winterSeasonDisplay(season, year, fullYear) : nonWinterSeasonDisplay(season, year)
  );
};

const SeasonSortOrder: { [key in Season]: number } = {
  [Season.WINTER]: 0,
  [Season.FALL]: 1,
  [Season.SUMMER]: 2,
  [Season.SPRING]: 3,
};

export const compareSeasons = (a: Season, b: Season) => SeasonSortOrder[a] - SeasonSortOrder[b];

export const abbreviateName = (name: string): string => {
  if (name.startsWith("TBD")) {
    return "TBD";
  }

  let shortName = name.toUpperCase().trim();
  if (shortName.startsWith("THE ")) {
    shortName = shortName.substring(3);
  }
  shortName = shortName.replace(/\s/g, "");
  if (shortName.length <= 4) {
    return shortName;
  }
  const firstCharacter = shortName.substring(0, 1);
  let restOfString = shortName.substring(1);
  restOfString = restOfString.replace(/[AEIOU]/g, "");
  let abbreviation = firstCharacter + restOfString;
  if (abbreviation.length > 4) {
    abbreviation = abbreviation.substring(0, 4);
  }
  return abbreviation;
};

export const convertMonthNumberToSeason = (month: number): Season => {
  switch (month) {
    case 0:
    case 1:
    case 2:
      return Season.WINTER;
    case 3:
    case 4:
    case 5:
      return Season.SPRING;
    case 6:
    case 7:
    case 8:
      return Season.SUMMER;
    case 9:
    case 10:
    case 11:
      return Season.FALL;
    default:
      throw new Error("Passed in a month integer that does not represent an actual month");
  }
};

export const checkCurrSeasonYear = (month: number, year: number): number => {
  if (convertMonthNumberToSeason(month) === Season.WINTER) return year - 1;
  return year;
};

export const getNextSeason = ({ season, year: currYear }: SeasonAndYear): SeasonAndYear => {
  let year = currYear;
  let nextSeason: Season;

  switch (season) {
    case Season.WINTER:
      nextSeason = Season.SPRING;
      year += 1;
      break;
    case Season.SPRING:
      nextSeason = Season.SUMMER;
      break;
    case Season.SUMMER:
      nextSeason = Season.FALL;
      break;
    case Season.FALL:
      nextSeason = Season.WINTER;
      break;
    default:
      throw new Error("Unrecognized Season");
  }

  return {
    season: nextSeason,
    year: year,
  };
};

export const formatSport = (sport: Sport): string => {
  switch (sport) {
    case Sport.BASEBALL:
      return "Baseball";
    case Sport.SOFTBALL:
      return "Softball";
    default:
      throw new Error(`Unknown sport, ${sport}`);
  }
};

export const playerDisplayName = (player: Team.Player, variant: "normal" | "short" = "normal") => {
  switch (variant) {
    case "normal":
      return buildDisplayName(player.first_name, player.last_name, `${player.number}`);
    case "short":
      return buildShortDisplayName(player.first_name, player.last_name, `${player.number}`);
  }
};
